import axios from 'axios'
import { clearAllAuthStorage, getAuthToken } from "@/modules/auth/services/auth.storage";
import { getAppModeFromEnv, isVendorMode } from "@/shared/utils/appMode";

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
}

defaultHeaders["App-Mode"] = getAppModeFromEnv();

function getStoredAppCode(): string {
  try {
    if (typeof window === "undefined") return "";
    return String(window.localStorage?.getItem("app-code") || "").trim();
  } catch {
    return "";
  }
}

const envAppCode = String(process.env.VUE_APP_CODE || "").trim();
const initialAppCode = getStoredAppCode() || envAppCode;

if (!isVendorMode() && initialAppCode) defaultHeaders["App-Code"] = initialAppCode;

const http = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'https://api-test.systha.com/api/v1/platform',
  timeout: 15000,
  headers: defaultHeaders
})

export function syncAppCodeHeader() {
  if (isVendorMode()) {
    delete http.defaults.headers.common["App-Code"];
    return;
  }
  const appCode = getStoredAppCode() || envAppCode;
  if (appCode) {
    http.defaults.headers.common["App-Code"] = appCode;
  } else {
    delete http.defaults.headers.common["App-Code"];
  }
}

http.interceptors.request.use((config) => {
  // Keep App-Code in sync with latest selected company code.
  if (!isVendorMode()) {
    const appCode = getStoredAppCode() || envAppCode;
    if (appCode) {
      (config.headers as any)["App-Code"] = appCode;
    }
  } else {
    if (config.headers) delete (config.headers as any)["App-Code"];
  }

  const hasAuthorizationHeader = Boolean(config.headers?.Authorization)
  if (!hasAuthorizationHeader) {
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearAllAuthStorage()
      delete http.defaults.headers.common.Authorization
      syncAppCodeHeader()
      window.dispatchEvent(new Event('auth-changed'))

      const currentHash = window.location.hash.replace(/^#/, '') || '/'
      const isAuthRoute =
        currentHash.startsWith('/login') ||
        currentHash.startsWith('/signup') ||
        currentHash.startsWith('/reset-password')

      if (!isAuthRoute) {
        const redirect = encodeURIComponent(currentHash)
        window.location.hash = `/login?redirect=${redirect}`
      }
    }

    return Promise.reject(error)
  }
)

export default http
